import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Switch } from './common/Catalyst/switch';
import { Text } from './common/Catalyst/text';
import { Badge } from './common/Catalyst/badge';
import { bankService, jobsService } from './clients/ApiClients';
import { JobRunTargetReturn } from './clients/rust-api-client';
import { getBankErrorParts } from './utils/bankHelpers';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canSubmitPayerFile } from './constants/UserTypes';

interface Props {
  hoaId: string;
  /** From `HoaReturn.payer_file_auto_upload`. */
  enabled: boolean;
  /** Lets the parent re-read the HOA once the switch has been flipped. */
  onChanged?: (enabled: boolean) => void;
}

/**
 * Whether this association's payer file goes out on its own each morning.
 *
 * Two gates govern a scheduled upload and this is only one of them — the
 * CronJob itself ships suspended per environment. So "on" here means
 * "include this association when the job runs", not "the job runs".
 * The copy says so, because a switch that reads as armed while nothing
 * is scheduled is the kind of thing people rely on and then discover.
 */

/**
 * `skip_reason` values that mean "nothing to do", as opposed to
 * something a person should act on.
 *
 * In a healthy setup the overwhelming majority of history rows are
 * `already_submitted_today` — the 09:00 and 11:00 firings correctly
 * doing nothing. Showing those as prominently as a real problem trains
 * people to ignore the panel, so they are summarised rather than
 * listed.
 */
const ROUTINE_SKIPS = new Set([
  'already_submitted_today',
  'not_enabled',
  'hoa_inactive',
]);

/**
 * What a failure means, by class.
 *
 * `error_message` is whatever the failing layer said — a bank API
 * rejection, or on one memorable occasion a Postgres constraint name.
 * Neither is something an Owner or Admin can act on, and the second
 * leaks schema detail into a customer-facing screen. So the class
 * carries the meaning and the raw text sits underneath it for whoever
 * is actually debugging.
 */
const FAILURE_SUMMARY: Record<string, string> = {
  auth: 'The bank rejected our credentials, so this connection is paused for the rest of the day. It will try again tomorrow — repeated attempts risk locking the account.',
  transient:
    "We couldn't reach the bank. The job will try again on its next run.",
  permanent:
    'The bank would not accept this file, and retrying will not change that.',
  none: 'The run did not complete.',
};

/** Reasons a manager can actually do something about. */
const SKIP_HELP: Record<string, string> = {
  hoa_has_no_bank:
    'Assign a bank account to this HOA — that is also what mints its Entity ID.',
  no_lockbox_id: 'The bank connection has no Lockbox Management CO ID set.',
  no_association_id:
    'This HOA has no Entity ID on its bank connection. Re-assign its bank account.',
  connection_inactive: 'This HOA’s bank connection is switched off.',
  no_homes: 'This HOA has no homes, so there is nothing to send.',
  no_company: 'This HOA is not attached to a management company.',
  connection_auth_blocked:
    'The bank rejected the credentials twice today, so the connection is paused until tomorrow. Fix the credentials — do not retry.',
  permanent_error_today:
    'A run failed earlier today for a reason that will not fix itself by retrying.',
};

const formatWhen = (epochSeconds?: number | null): string => {
  if (!epochSeconds) return '';
  return new Date(epochSeconds * 1000).toLocaleString();
};

const PayerAutoUploadPanel: React.FC<Props> = ({
  hoaId,
  enabled,
  onChanged,
}) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canSubmit = useMemo(() => canSubmitPayerFile(userType), [userType]);

  const [on, setOn] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<JobRunTargetReturn[]>([]);

  useEffect(() => setOn(enabled), [enabled]);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await jobsService.hoaHistory({ hoaId, limit: 25 });
      setHistory(data.targets ?? []);
    } catch (err) {
      // A missing history is not worth an alert — the switch is still
      // usable without it.
      console.error('Could not load scheduled-upload history:', err);
      setHistory([]);
    }
  }, [hoaId]);

  useEffect(() => {
    if (canSubmit) fetchHistory();
  }, [canSubmit, fetchHistory]);

  const toggle = async (next: boolean) => {
    try {
      setSaving(true);
      setError(null);
      const { data } = await bankService.setPayerAutoUpload({
        hoaId,
        payerAutoUploadUpdate: { enabled: next },
      });
      setOn(data.enabled);
      onChanged?.(data.enabled);
      fetchHistory();
    } catch (err) {
      // Shown verbatim on purpose. "This HOA has no bank assigned" is a
      // different answer from "it is switched off", and flattening both
      // into an unchecked box is how somebody concludes the feature is
      // broken.
      const { body } = getBankErrorParts(err);
      setError(body || "Couldn't change this setting. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!canSubmit) return null;

  const latest = history[0];

  // History is newest-first, so everything before the most recent
  // success happened *after* it.
  const lastSentIndex = history.findIndex((t) => t.status === 'succeeded');
  const lastSent = lastSentIndex === -1 ? undefined : history[lastSentIndex];

  // Only surface a problem that has not since been superseded by a
  // successful send. A failure followed by a success is history, and
  // leaving it on screen reads as "this is broken now" long after it
  // was fixed.
  const sinceLastSuccess =
    lastSentIndex === -1 ? history : history.slice(0, lastSentIndex);
  const attention = sinceLastSuccess.find(
    (t) =>
      t.status === 'failed' ||
      (t.status === 'skipped' &&
        t.skip_reason &&
        !ROUTINE_SKIPS.has(t.skip_reason))
  );

  return (
    <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden mb-4">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Text className="font-semibold">Send this file automatically</Text>
            <Text className="mt-1 text-sm">
              Include this HOA in the daily payer file job. The bank expects a
              roster every morning, whether or not anything changed.
            </Text>
          </div>
          <Switch
            checked={on}
            onChange={toggle}
            disabled={saving}
            aria-label="Send this HOA’s payer file automatically"
          />
        </div>

        <ErrorAlert className="mt-3">{error}</ErrorAlert>

        {/* The association's current standing, rather than a wall of rows.
            Most history is the later firings correctly doing nothing. */}
        <div className="mt-4 space-y-1">
          {lastSent ? (
            <Text className="text-sm">
              Last sent {formatWhen(lastSent.date_created)}
              {lastSent.result_ref ? ` · ${lastSent.result_ref}` : ''}
            </Text>
          ) : (
            <Text className="text-sm">
              {on
                ? 'No file has gone out on a schedule yet.'
                : 'Scheduled sending is off for this HOA.'}
            </Text>
          )}

          {attention && (
            <div className="mt-2 rounded-md bg-amber-50 dark:bg-amber-900/30 px-3 py-2">
              <div className="flex items-center gap-2">
                <Badge color={attention.status === 'failed' ? 'red' : 'amber'}>
                  {attention.status === 'failed' ? 'Failed' : 'Skipped'}
                </Badge>
                <Text className="text-sm">
                  {formatWhen(attention.date_created)}
                </Text>
              </div>
              <Text className="mt-1 text-sm">
                {attention.status === 'failed'
                  ? FAILURE_SUMMARY[attention.error_class] ||
                    FAILURE_SUMMARY.none
                  : SKIP_HELP[attention.skip_reason ?? ''] ||
                    `Skipped: ${attention.skip_reason}`}
              </Text>
              {attention.status === 'failed' && attention.error_message && (
                <Text className="mt-1 text-xs font-mono opacity-60 break-all">
                  {attention.error_message}
                </Text>
              )}
            </div>
          )}

          {!attention && latest?.skip_reason === 'already_submitted_today' && (
            <Text className="text-sm">
              Today’s file has already been sent; later runs today will skip
              this HOA.
            </Text>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayerAutoUploadPanel;
